<script setup lang="ts">
import { useHelp } from '@/composables/useHelp';

const emit = defineEmits<{
  (e: 'navigate', articleId: string): void;
}>();

const {
  categories,
  articlesByCategory,
  currentArticleId,
  tocExpanded,
  toggleTocCategory,
} = useHelp();

function handleArticleClick(articleId: string) {
  emit('navigate', articleId);
}

function isActive(articleId: string): boolean {
  return currentArticleId.value === articleId;
}
</script>

<template>
  <nav class="toc">
    <div class="toc-header">
      <span>Contents</span>
    </div>
    <div class="toc-categories">
      <div
        v-for="category in categories"
        :key="category"
        class="toc-category"
      >
        <button
          class="toc-category-header"
          @click="toggleTocCategory(category)"
          :class="{ expanded: tocExpanded[category] }"
        >
          <i
            class="bi"
            :class="tocExpanded[category] ? 'bi-chevron-down' : 'bi-chevron-right'"
          ></i>
          <span>{{ category }}</span>
        </button>
        <Transition name="toc-expand">
          <div v-if="tocExpanded[category]" class="toc-articles">
            <button
              v-for="article in articlesByCategory[category]"
              :key="article.id"
              class="toc-article"
              :class="{ active: isActive(article.id) }"
              @click="handleArticleClick(article.id)"
            >
              {{ article.title }}
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.toc {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toc-header {
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
}

.toc-categories {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.toc-category {
  margin-bottom: 2px;
}

.toc-category-header {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s;
}

.toc-category-header:hover {
  background: #f3f4f6;
}

.toc-category-header i {
  font-size: 10px;
  color: #9ca3af;
  transition: transform 0.15s;
}

.toc-articles {
  overflow: hidden;
}

.toc-article {
  display: block;
  width: 100%;
  padding: 6px 16px 6px 32px;
  background: none;
  border: none;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  border-left: 2px solid transparent;
}

.toc-article:hover {
  background: #f3f4f6;
  color: #374151;
}

.toc-article.active {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
  border-left-color: #2563eb;
  font-weight: 500;
}

/* Expand transition */
.toc-expand-enter-active,
.toc-expand-leave-active {
  transition: all 0.2s ease;
}

.toc-expand-enter-from,
.toc-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.toc-expand-enter-to,
.toc-expand-leave-from {
  opacity: 1;
  max-height: 500px;
}
</style>

<!-- Dark mode styles (unscoped) -->
<style>
[data-bs-theme="dark"] .toc-header {
  color: #94a3b8;
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .toc-category-header {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .toc-category-header:hover {
  background: #334155;
}

[data-bs-theme="dark"] .toc-category-header i {
  color: #64748b;
}

[data-bs-theme="dark"] .toc-article {
  color: #94a3b8;
}

[data-bs-theme="dark"] .toc-article:hover {
  background: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .toc-article.active {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
  border-left-color: #60a5fa;
}
</style>
